import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CommercialApiService, CommercialModuleDefinition } from './commercial-api.service';

@Component({
  standalone: true,
  selector: 'ain-commercial-modules',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    FormsModule,
    InputTextModule
  ],
  template: `
    <ain-page [title]="t('commercial.modules.title')" [description]="t('commercial.modules.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'commercial.modules.help.title'"
        [descriptionKey]="'commercial.modules.help.description'"
        [bulletKeys]="[
          'commercial.modules.help.bullets.keys',
          'commercial.modules.help.bullets.plans'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('commercial.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('commercial.errorLoad')" [message]="error() ?? ''" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('commercial.modules.create')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('commercial.actions.refresh')" (click)="load()"></button>
          </div>
          @if (info()) {
            <p class="info">{{ info() }}</p>
          }
          @if (rows().length === 0) {
            <ain-state type="empty" [title]="t('commercial.modules.empty')" />
          } @else {
            <p-table [value]="rows()" dataKey="id" [paginator]="true" [rows]="15">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('commercial.modules.fields.moduleKey') }}</th>
                  <th>{{ t('commercial.modules.fields.name') }}</th>
                  <th>{{ t('commercial.modules.fields.description') }}</th>
                  <th>{{ t('commercial.modules.fields.status') }}</th>
                  <th>{{ t('commercial.modules.fields.sortOrder') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.moduleKey }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.description || '—' }}</td>
                  <td>{{ statusLabel(row.status) }}</td>
                  <td>{{ row.sortOrder }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }

      <p-dialog [header]="t('commercial.modules.create')" [(visible)]="createVisible" [modal]="true" [style]="{ width: '520px' }">
        <div class="form-grid">
          <label>
            <span>{{ t('commercial.modules.fields.moduleKey') }}</span>
            <input pInputText [(ngModel)]="createForm.moduleKey" />
          </label>
          <label>
            <span>{{ t('commercial.modules.fields.name') }}</span>
            <input pInputText [(ngModel)]="createForm.name" />
          </label>
          <label class="full">
            <span>{{ t('commercial.modules.fields.description') }}</span>
            <input pInputText [(ngModel)]="createForm.description" />
          </label>
          <label>
            <span>{{ t('commercial.modules.fields.sortOrder') }}</span>
            <input pInputText type="number" [(ngModel)]="createForm.sortOrder" />
          </label>
        </div>
        @if (createError()) {
          <p class="error">{{ createError() }}</p>
        }
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveCreate()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid var(--ain-color-border, #e5e7eb); border-radius: 14px; padding: 16px; background: var(--ain-color-surface); }
    .toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .form-grid label { display: flex; flex-direction: column; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .info { margin: 0 0 12px; color: var(--ain-color-text-muted, #64748b); }
    .error { color: #b91c1c; margin-top: 8px; }
  `]
})
export class CommercialModulesComponent implements OnInit {
  private readonly api = inject(CommercialApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly rows = signal<CommercialModuleDefinition[]>([]);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly info = signal<string | null>(null);
  readonly saving = signal(false);
  readonly createError = signal<string | null>(null);

  createVisible = false;
  createForm = { moduleKey: '', name: '', description: '', sortOrder: 0 };

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.listModules().subscribe({
      next: items => {
        this.rows.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('commercial.modules.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  openCreate(): void {
    this.createForm = { moduleKey: '', name: '', description: '', sortOrder: this.rows().length + 1 };
    this.createError.set(null);
    this.createVisible = true;
  }

  saveCreate(): void {
    if (!this.createForm.moduleKey.trim() || !this.createForm.name.trim()) {
      this.createError.set(this.t('commercial.validation.required'));
      return;
    }

    this.saving.set(true);
    this.createError.set(null);
    this.api.createModule({
      moduleKey: this.createForm.moduleKey.trim(),
      name: this.createForm.name.trim(),
      description: this.createForm.description.trim(),
      sortOrder: Number(this.createForm.sortOrder) || 0
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.createVisible = false;
        this.info.set(this.t('commercial.modules.created'));
        this.load();
      },
      error: (err: HttpErrorResponse) => {
        this.saving.set(false);
        this.createError.set(this.resolveError(err, 'commercial.modules.errorCreate'));
      }
    });
  }

  statusLabel(status: string): string {
    const key = `commercial.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  private resolveError(error: HttpErrorResponse, fallbackKey: string): string {
    if (error.status === 409) {
      return this.t('commercial.errors.conflict');
    }
    return this.t(fallbackKey);
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }
}
